import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
} from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Subject, Observable, combineLatest, EMPTY } from 'rxjs';
import {
  takeUntil,
  map,
  switchMap,
  catchError,
  distinctUntilChanged,
  tap,
} from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { AuthService } from 'src/app/service/auth.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { ScannerInsightsDataService } from './scanner-insights-data.service';
import {
  ScannerInsightsData,
  InsightsLoadingState,
  SentimentEvent,
  ScoreLabEvent,
  MosaicScoreData,
} from './scanner-insights.models';
import { SCANNER_INSIGHTS_REFRESH_PARAMS } from '../scanner.constants';

@Component({
  selector: 'app-scanner-insights',
  templateUrl: './scanner-insights.component.html',
  styleUrls: ['./scanner-insights.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScannerInsightsComponent implements OnInit, OnDestroy {
  insightsData: ScannerInsightsData | null = null;

  loadingState: InsightsLoadingState = {
    loading: false,
    ready: false,
    noData: false,
    hasError: false,
  };

  isCompanyTag = false;
  isPneTag = false;

  currentParams: Params = {};

  screenSize$ = this.breakpointService.screenSize$;

  /** Reactive score loading state from shared service */
  scoreLoading$ = this.insightsDataService.scoreLoading$;

  /** Reactive mosaic score derived from shared score state */
  mosaicScore$: Observable<MosaicScoreData> =
    this.insightsDataService.scoreState$.pipe(
      map((state) =>
        this.insightsDataService.getMosaicScoreFromState(
          state,
          this.isSentiment,
        ),
      ),
    );

  /** Combined loading state: true if either score or events are loading */
  combinedLoading$: Observable<boolean> = combineLatest([
    this.scoreLoading$,
  ]).pipe(map(([scoreLoading]) => scoreLoading || this.loadingState.loading));

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public routing: RoutingService,
    public auth: AuthService,
    private breakpointService: BreakpointService,
    private insightsDataService: ScannerInsightsDataService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        tap((params) => {
          this.currentParams = params;
          this.cdr.markForCheck();
        }),
        distinctUntilChanged(
          (prev, curr) =>
            !this.routing.reFresh(
              curr,
              prev,
              SCANNER_INSIGHTS_REFRESH_PARAMS as unknown as string[],
            ),
        ),
        switchMap((params) => {
          this.updateTagState(params);
          this.setLoadingState(true);
          return this.insightsDataService.fetchInsightsData(params).pipe(
            catchError(() => {
              this.handleDataError();
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => this.handleDataSuccess(data));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleDataSuccess(data: ScannerInsightsData): void {
    this.insightsData = data;
    const hasData = data.isSentiment
      ? this.insightsDataService.hasNonEmptyEvents(data.events || [])
      : this.insightsDataService.hasNonEmptyScoreEvents(data.scoreEvents || []);

    this.loadingState = {
      loading: false,
      ready: hasData,
      noData: !hasData,
      hasError: false,
    };

    this.cdr.markForCheck();
  }

  private handleDataError(): void {
    this.loadingState = {
      loading: false,
      ready: false,
      noData: true,
      hasError: true,
      errorMessage: 'Failed to load insights data',
    };

    this.cdr.markForCheck();
  }

  private setLoadingState(loading: boolean): void {
    this.loadingState = {
      loading,
      ready: false,
      noData: false,
      hasError: false,
    };
    this.cdr.markForCheck();
  }

  private updateTagState(params: Params): void {
    if (!params['tag']) {
      this.isCompanyTag = false;
      this.isPneTag = false;
      return;
    }

    const tagType = params['tag'].split(':')[1];
    this.isCompanyTag = tagType === 'company';
    this.isPneTag = tagType === 'pne';
  }

  calculateBarPercentage(events: SentimentEvent[], value: number): number {
    return this.insightsDataService.calculateBarPercentage(events, value);
  }

  calculateScoreBarPercentage(events: ScoreLabEvent[], value: number): number {
    return this.insightsDataService.calculateScoreBarPercentage(events, value);
  }

  onEventClick(item: SentimentEvent): void {
    const isCurrentlySelected = this.currentParams['eventid'] === item.event.id;
    const newstype =
      this.currentParams['tag'] && item.event.id
        ? 'Events'
        : this.currentParams['newstype'];

    this.router.navigate([], {
      queryParams: {
        eventid: isCurrentlySelected ? null : item.event.id,
        eventype: isCurrentlySelected ? null : item.event.type,
        newstype: isCurrentlySelected ? null : newstype,
        newsday: null,
        eventsFromScanner: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  onScoreEventClick(item: ScoreLabEvent): void {
    const isCurrentlySelected = this.currentParams['eventid'] === item.id;
    const newstype =
      this.currentParams['tag'] && item.id
        ? 'Events'
        : this.currentParams['newstype'];

    this.router.navigate([], {
      queryParams: {
        eventid: isCurrentlySelected ? null : item.id,
        eventype: isCurrentlySelected ? null : item.type,
        scoreevent: isCurrentlySelected ? null : true,
        newstype: isCurrentlySelected ? null : newstype,
        newsday: null,
        eventsFromScanner: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  get isSentiment(): boolean {
    return this.auth.scorelabSettings.defaultScore === 'sentiment';
  }

  get mosaicScoreDisplay(): string {
    return this.insightsData?.mosaicScore?.displayValue || '';
  }

  get mosaicScoreValue(): number | undefined {
    return this.insightsData?.mosaicScore?.value;
  }

  get events(): SentimentEvent[] {
    return this.insightsData?.events || [];
  }

  get scoreEvents(): ScoreLabEvent[] {
    return this.insightsData?.scoreEvents || [];
  }
}
